import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardActions, CardContent, CardMedia, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, Grid, IconButton, LinearProgress, MenuItem, Paper, Snackbar, Stack, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Campaign as CampaignIcon, LocalOffer as PromoIcon,
  Groups as MembersIcon, Public as PublicIcon,
} from '@mui/icons-material';
import LoyaltyService from '../../services/loyalty.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { apiError } from './loyaltyUtils';
import LoyaltyImagePicker from './LoyaltyImagePicker';

const empty = { type: 'promo', title: '', body: '', audience: 'members', active: true, starts_at: '', ends_at: '' };

// The API stores date-only windows as whole days in Guatemala time.
const LOCAL_TZ = 'America/Guatemala';
const toInput = (v) => (v ? new Date(v).toLocaleDateString('en-CA', { timeZone: LOCAL_TZ }) : '');
const toLabel = (v) => (v ? new Date(v).toLocaleDateString(undefined, { timeZone: LOCAL_TZ, dateStyle: 'medium' }) : '');

const STATUS_COLOR = { live: 'success', scheduled: 'info', expired: 'default', inactive: 'default' };

const TypeIcon = ({ type, ...props }) => (type === 'promo' ? <PromoIcon {...props} /> : <CampaignIcon {...props} />);

/**
 * How the post looks in the customer app (mirrors the mobile card).
 */
const PromotionPreview = ({ form, imageUrl, t }) => {
  const theme = useTheme();
  const accent = form.type === 'promo' ? theme.palette.warning.main : theme.palette.primary.main;
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
      {imageUrl ? (
        <Box component="img" src={imageUrl} alt="" sx={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
      ) : (
        <Box sx={{ height: 72, bgcolor: alpha(accent, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TypeIcon type={form.type} sx={{ color: accent, fontSize: 32 }} />
        </Box>
      )}
      <Box p={2}>
        <Chip
          size="small"
          icon={<TypeIcon type={form.type} />}
          label={t(form.type === 'promo' ? 'loyalty.promotionTypePromo' : 'loyalty.promotionTypeAnnouncement')}
          sx={{ bgcolor: alpha(accent, 0.12), color: accent, fontWeight: 600, mb: 1, '& .MuiChip-icon': { color: accent } }}
        />
        <Typography fontWeight={700}>{form.title || t('loyalty.promotionTitlePlaceholder')}</Typography>
        {form.body && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>{form.body}</Typography>}
        {form.ends_at && (
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            {t('loyalty.promotionEndsOn', { date: new Date(`${form.ends_at}T12:00:00`).toLocaleDateString(undefined, { dateStyle: 'medium' }) })}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const PromotionList = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('loyalty.manage');

  const [promotions, setPromotions] = useState([]);
  const [maxLive, setMaxLive] = useState(5);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({ open: false, uuid: null });
  const [form, setForm] = useState(empty);
  const [image, setImage] = useState({ file: null, url: null, removed: false, error: null });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const show = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await LoyaltyService.getPromotions();
      setPromotions(res.data?.data || []);
      if (res.data?.meta?.max_live) setMaxLive(res.data.meta.max_live);
    } catch (error) {
      show(apiError(error, t('loyalty.errorLoading')), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Live + scheduled count toward the per-business cap.
  const used = promotions.filter((p) => p.status === 'live' || p.status === 'scheduled').length;
  const visible = useMemo(() => {
    if (filter === 'all') return promotions;
    if (filter === 'ended') return promotions.filter((p) => p.status === 'expired' || p.status === 'inactive');
    return promotions.filter((p) => p.status === filter);
  }, [promotions, filter]);

  const previewUrl = useMemo(
    () => (image.file ? URL.createObjectURL(image.file) : (!image.removed && image.url) || null),
    [image.file, image.url, image.removed],
  );
  useEffect(() => () => { if (image.file && previewUrl) URL.revokeObjectURL(previewUrl); }, [image.file, previewUrl]);

  const openNew = () => { setForm(empty); setImage({ file: null, url: null, removed: false, error: null }); setDialog({ open: true, uuid: null }); };
  const openEdit = (p) => {
    setForm({
      type: p.type, title: p.title, body: p.body || '', audience: p.audience, active: !!p.active,
      starts_at: toInput(p.starts_at), ends_at: toInput(p.ends_at),
    });
    setImage({ file: null, url: p.image_url || null, removed: false, error: null });
    setDialog({ open: true, uuid: p.uuid });
  };

  const save = async () => {
    if (!form.title.trim()) {
      show(t('common.snackbar.pleaseFillAllRequiredFields'), 'error');
      return;
    }
    const payload = { ...form, starts_at: form.starts_at || null, ends_at: form.ends_at || null };
    try {
      setSaving(true);
      let uuid = dialog.uuid;
      if (uuid) {
        await LoyaltyService.updatePromotion(uuid, payload);
      } else {
        const res = await LoyaltyService.createPromotion(payload);
        uuid = res.data?.data?.uuid;
      }
      if (uuid && image.file) {
        await LoyaltyService.uploadPromotionImage(uuid, image.file);
      } else if (uuid && image.removed && image.url) {
        await LoyaltyService.removePromotionImage(uuid);
      }
      setDialog({ open: false, uuid: null });
      show(t('loyalty.promotionSaved'));
      load();
    } catch (error) {
      show(apiError(error, t('loyalty.errorSaving')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await LoyaltyService.updatePromotion(p.uuid, { active: !p.active });
      load();
    } catch (error) {
      show(apiError(error, t('loyalty.errorSaving')), 'error');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t('loyalty.deletePromotionConfirm'))) return;
    try {
      await LoyaltyService.deletePromotion(p.uuid);
      show(t('loyalty.promotionDeleted'));
      load();
    } catch (error) {
      show(apiError(error, t('loyalty.errorSaving')), 'error');
    }
  };

  const windowLabel = (p) => {
    if (!p.starts_at && !p.ends_at) return t('loyalty.promotionNoEnd');
    return `${toLabel(p.starts_at) || '…'} → ${toLabel(p.ends_at) || '…'}`;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">{t('loyalty.promotions')}</Typography>
          <Typography color="text.secondary">{t('loyalty.promotionsSubtitle')}</Typography>
        </Box>
        {canManage && (
          <Tooltip title={used >= maxLive ? t('loyalty.promotionLimitHint', { max: maxLive }) : ''}>
            <span>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>{t('loyalty.newPromotion')}</Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" my={2} flexWrap="wrap" gap={2}>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
          <ToggleButton value="all">{t('loyalty.promotionFilterAll')}</ToggleButton>
          <ToggleButton value="live">{t('loyalty.promotionStatus.live')}</ToggleButton>
          <ToggleButton value="scheduled">{t('loyalty.promotionStatus.scheduled')}</ToggleButton>
          <ToggleButton value="ended">{t('loyalty.promotionFilterEnded')}</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary">{t('loyalty.promotionUsage', { used, max: maxLive })}</Typography>
          <LinearProgress variant="determinate" value={Math.min(100, (used / maxLive) * 100)} color={used >= maxLive ? 'warning' : 'primary'} sx={{ borderRadius: 1, height: 6 }} />
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : visible.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
          <CampaignIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography fontWeight={600} mt={1}>{t('loyalty.noPromotions')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('loyalty.noPromotionsHint')}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {visible.map((p) => (
            <Grid key={p.uuid} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, opacity: p.status === 'expired' || p.status === 'inactive' ? 0.7 : 1 }}>
                {p.image_url && <CardMedia component="img" height="140" image={p.image_url} alt="" />}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" icon={<TypeIcon type={p.type} />} label={t(p.type === 'promo' ? 'loyalty.promotionTypePromo' : 'loyalty.promotionTypeAnnouncement')} />
                    <Chip size="small" color={STATUS_COLOR[p.status]} label={t(`loyalty.promotionStatus.${p.status}`)} />
                    <Chip size="small" variant="outlined" icon={p.audience === 'public' ? <PublicIcon /> : <MembersIcon />} label={t(p.audience === 'public' ? 'loyalty.audiencePublic' : 'loyalty.audienceMembers')} />
                  </Stack>
                  <Typography fontWeight={700}>{p.title}</Typography>
                  {p.body && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {p.body}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>{windowLabel(p)}</Typography>
                </CardContent>
                {canManage && (
                  <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                    <FormControlLabel
                      control={<Switch size="small" checked={!!p.active} onChange={() => toggleActive(p)} />}
                      label={<Typography variant="body2">{t('common.active')}</Typography>}
                    />
                    <Box>
                      <IconButton size="small" onClick={() => openEdit(p)}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => remove(p)}><DeleteIcon /></IconButton>
                    </Box>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, uuid: null })} maxWidth="md" fullWidth>
        <DialogTitle>{dialog.uuid ? t('loyalty.editPromotion') : t('loyalty.newPromotion')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <ToggleButtonGroup fullWidth size="small" exclusive value={form.type} onChange={(_, v) => v && setForm({ ...form, type: v })}>
                    <ToggleButton value="promo"><PromoIcon fontSize="small" sx={{ mr: 1 }} />{t('loyalty.promotionTypePromo')}</ToggleButton>
                    <ToggleButton value="announcement"><CampaignIcon fontSize="small" sx={{ mr: 1 }} />{t('loyalty.promotionTypeAnnouncement')}</ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth required label={t('loyalty.promotionTitle')} value={form.title} inputProps={{ maxLength: 120 }}
                    onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth multiline rows={3} label={t('loyalty.promotionBody')} value={form.body} inputProps={{ maxLength: 2000 }}
                    onChange={(e) => setForm({ ...form, body: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField select fullWidth label={t('loyalty.audience')} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}
                    helperText={t(form.audience === 'public' ? 'loyalty.audiencePublicHint' : 'loyalty.audienceMembersHint')}>
                    <MenuItem value="members">{t('loyalty.audienceMembers')}</MenuItem>
                    <MenuItem value="public">{t('loyalty.audiencePublic')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth type="date" label={t('loyalty.startsAt')} InputLabelProps={{ shrink: true }} value={form.starts_at}
                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth type="date" label={t('loyalty.endsAt')} InputLabelProps={{ shrink: true }} value={form.ends_at}
                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <LoyaltyImagePicker value={image} onChange={setImage} disabled={saving} t={t} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />} label={t('loyalty.promotionPublish')} />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="overline" color="text.secondary">{t('loyalty.promotionPreview')}</Typography>
              <PromotionPreview form={form} imageUrl={previewUrl} t={t} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, uuid: null })}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PromotionList;
